import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { IconSend, IconTrophy, IconSearch } from '@tabler/icons-react';
import { useMantineTheme } from '@mantine/core';
import { useContext, useMemo } from 'react';

import type { ContestEmptyStateAction } from '~/domains/contest/domains/common/components/ContestEmptyStateCard/types';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { UserContext } from '~/components/providers/UserProvider';

import { ContestStatusFilter } from '../../services/enums/contest-status-filter.enum';
import { ContestTab } from './useEntriesTabsProps';

interface UseMyContestsEmptyConfigProps {
  contestTab: ContestTab;
}

interface MyContestsEmptyConfig {
  title: string;
  text: string;
  actions?: ContestEmptyStateAction[];
  icon?: React.ReactNode;
}

const useMyContestsEmptyConfig = ({ contestTab }: UseMyContestsEmptyConfigProps) => {
  const { isUserSignedIn } = useContext(UserContext);
  const { t } = useTranslation('contest');
  const router = useRouter();
  const { query } = router;
  const theme = useMantineTheme();

  return useMemo((): MyContestsEmptyConfig => {
    const configs = {
      [ContestTab.ACTIVE]: {
        title: t('entries.states.empty.active.title'),
        text: t('entries.states.empty.active.text'),
        icon: <IconTrophy size={60} color={theme.colors.gray[4]} />,
        actions: [
          {
            text: t('entries.states.empty.buttonText.discoverContests'),
            onClick: () => router.push(Routes.contestLobby()),
            variant: 'secondary-fill' as const,
            fullWidth: true,
          },
        ],
      },
      [ContestTab.FINAL]: {
        title: t('entries.states.empty.finalized.title'),
        text: t('entries.states.empty.finalized.text'),
        icon: <IconTrophy size={60} color={theme.colors.gray[4]} />,
        actions: [],
      },
      [ContestTab.INVITES]: {
        title: t('entries.states.empty.invites.title'),
        text: t('entries.states.empty.invites.text'),
        actions: [],
        icon: <IconSend size={60} color={theme.colors.gray[4]} />,
      },
      filtered: {
        title: t('entries.states.empty.filtered.title'),
        text: t('entries.states.empty.filtered.text'),
        actions: [],
        icon: <IconSearch size={60} color={theme.colors.gray[4]} />,
      },
    };

    const signedOutConfig = {
      title: t('entries.states.empty.signedOut.title'),
      text: t('entries.states.empty.signedOut.text'),
      actions: [
        {
          text: t('entries.states.empty.buttonText.signUp'),
          onClick: () => router.push(Routes.signUp()),
          variant: 'secondary-fill' as const,
          fullWidth: true,
        },
        {
          text: t('entries.states.empty.buttonText.signIn'),
          onClick: () => router.push(Routes.signIn()),
          variant: 'tertiary-fill' as const,
          fullWidth: true,
        },
      ],
    };

  // Logic for determining if there are any filters applied
  if (
    query[URLQueryKeys.CONTEST_STATUS]
    || query[URLQueryKeys.CONTEST_TYPE]
    || query[URLQueryKeys.LEAGUES]
  ) {
    return configs.filtered;
  }

    return isUserSignedIn
      ? configs[contestTab] || configs[ContestStatusFilter.ACTIVE]
      : signedOutConfig;
  }, [t, contestTab, isUserSignedIn, router]);
};

export default useMyContestsEmptyConfig;
